import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Search, BookOpen, Trophy, Tv, Vote, Sparkles, ShieldCheck, Wrench, MessageSquare } from "lucide-react";
import { RouteTopBar } from "@/components/layout/RouteTopBar";
import { LegalFooter } from "@/components/brand/LegalFooter";
import { useLanguage } from "@/hooks/useLanguage";
import { trackEvent } from "@/lib/audit";

export const Route = createFileRoute("/ayuda")({
  head: () => ({
    meta: [
      { title: "Centro de Ayuda · VerseSport™" },
      { name: "description", content: "Guías rápidas para sincronizar TV, encuestas, predicciones, puntos, Pulse, Insights y privacidad." },
      { property: "og:title", content: "Centro de Ayuda · VerseSport™" },
      { property: "og:description", content: "Aprende a usar VerseSport en minutos." },
    ],
  }),
  component: HelpPage,
});

type CategoryKey =
  | "tv_sync"
  | "polls"
  | "predictions"
  | "points"
  | "pulse"
  | "insights"
  | "privacy"
  | "troubleshooting";

interface FaqItem {
  id: string;
  category: CategoryKey;
  icon: typeof Tv;
  questionKey: string;
  answerKey: string;
}

const FAQS: FaqItem[] = [
  { id: "tv-1", category: "tv_sync",       icon: Tv,          questionKey: "help_tv_q",          answerKey: "help_tv_a" },
  { id: "po-1", category: "polls",         icon: Vote,        questionKey: "help_polls_q",       answerKey: "help_polls_a" },
  { id: "pr-1", category: "predictions",   icon: Trophy,      questionKey: "help_pred_q",        answerKey: "help_pred_a" },
  { id: "pt-1", category: "points",        icon: Sparkles,    questionKey: "help_points_q",      answerKey: "help_points_a" },
  { id: "pu-1", category: "pulse",         icon: MessageSquare, questionKey: "help_pulse_q",      answerKey: "help_pulse_a" },
  { id: "in-1", category: "insights",      icon: BookOpen,    questionKey: "help_insights_q",    answerKey: "help_insights_a" },
  { id: "pv-1", category: "privacy",       icon: ShieldCheck, questionKey: "help_privacy_q",     answerKey: "help_privacy_a" },
  { id: "tb-1", category: "troubleshooting", icon: Wrench,    questionKey: "help_trouble_q",     answerKey: "help_trouble_a" },
];

const CATEGORIES: { key: CategoryKey; labelKey: string; icon: typeof Tv }[] = [
  { key: "tv_sync",         labelKey: "help_cat_tv",         icon: Tv },
  { key: "polls",           labelKey: "help_cat_polls",      icon: Vote },
  { key: "predictions",     labelKey: "help_cat_predictions", icon: Trophy },
  { key: "points",          labelKey: "help_cat_points",     icon: Sparkles },
  { key: "pulse",           labelKey: "help_cat_pulse",      icon: MessageSquare },
  { key: "insights",        labelKey: "help_cat_insights",   icon: BookOpen },
  { key: "privacy",         labelKey: "help_cat_privacy",    icon: ShieldCheck },
  { key: "troubleshooting", labelKey: "help_cat_trouble",    icon: Wrench },
];

function HelpPage() {
  const { t } = useLanguage();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState<CategoryKey | "all">("all");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return FAQS.filter((f) => {
      if (active !== "all" && f.category !== active) return false;
      if (!q) return true;
      return (
        t(f.questionKey).toLowerCase().includes(q) ||
        t(f.answerKey).toLowerCase().includes(q)
      );
    });
  }, [query, active, t]);

  return (
    <main className="min-h-screen bg-background">
      <RouteTopBar fallback="/welcome" />
      <article className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        <header className="mb-6">
          <h1 className="text-3xl font-black text-foreground">{t("help_page_title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("help_page_subtitle")}</p>
        </header>

        <div className="relative mb-5">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              if (e.target.value.length === 3) void trackEvent("help_search", { surface: "ayuda_route" });
            }}
            placeholder={t("help_search_placeholder")}
            className="w-full rounded-xl border border-border/60 bg-background/80 py-2.5 pl-9 pr-3 text-sm"
            aria-label={t("help_search_placeholder")}
          />
        </div>

        <nav className="-mx-1 mb-6 flex flex-wrap gap-1.5">
          <button
            onClick={() => setActive("all")}
            className={`rounded-full px-3 py-1 text-[11px] font-bold transition ${active === "all" ? "bg-primary text-primary-foreground" : "bg-accent/30 hover:bg-accent/50"}`}
          >
            {t("help_cat_all")}
          </button>
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            return (
              <button
                key={c.key}
                onClick={() => {
                  setActive(c.key);
                  void trackEvent("help_category_opened", { category: c.key });
                }}
                className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-bold transition ${active === c.key ? "bg-primary text-primary-foreground" : "bg-accent/30 hover:bg-accent/50"}`}
              >
                <Icon className="h-3 w-3" /> {t(c.labelKey)}
              </button>
            );
          })}
        </nav>

        <ol className="space-y-3">
          {filtered.length === 0 ? (
            <li className="rounded-xl border border-dashed border-border/60 bg-accent/10 p-6 text-center text-sm text-muted-foreground">
              {t("help_empty_results")}
            </li>
          ) : (
            filtered.map((f) => {
              const Icon = f.icon;
              return (
                <li key={f.id} className="rounded-2xl border border-border/60 bg-background/70 p-4">
                  <div className="mb-1 flex items-center gap-2">
                    <Icon className="h-4 w-4 text-primary" />
                    <h2 className="text-sm font-bold">{t(f.questionKey)}</h2>
                  </div>
                  <p className="text-sm leading-relaxed text-muted-foreground">{t(f.answerKey)}</p>
                </li>
              );
            })
          )}
        </ol>

        <p className="mt-8 text-xs text-muted-foreground">
          {t("help_more_legal")}{" "}
          <Link to="/terminos" className="font-bold text-primary hover:underline">
            {t("legal_terms")}
          </Link>
          {" · "}
          <Link to="/privacidad" className="font-bold text-primary hover:underline">
            {t("legal_privacy")}
          </Link>
        </p>

        <LegalFooter />
      </article>
    </main>
  );
}